import { cn } from "@-/com.npmjs.cn";
import { Skeleton } from "@-/dev.improve.haniverse.components.ui.skeleton";
import { DeckPageItemRow } from "@-/dev.improve.haniverse.routes.nav.app.deck._components.item-row";
import { DeckSectionScrollContext } from "@-/dev.improve.haniverse.routes.nav.app.deck._components.deck-card-section.viewport";
import { useSectionAccordionMeasuredRowWindow } from "@-/dev.improve.haniverse.components.ui.section-accordion";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { useContext, useMemo, useState, type ReactElement } from "react";
import { useWindowDimensions, View } from "@-/com.npmjs.react-native";

export function DeckCardSectionListView({
  cards,
  footer,
  isEditing,
  onRemoveCard,
  pendingDetailsFronts,
}: {
  cards: MorphemeDetailsTileSlice[];
  footer?: ReactElement | null;
  isEditing: boolean;
  onRemoveCard?: (graphemeCard: MorphemeDetailsTileSlice) => void;
  /** Fronts whose `cards` entry is a provisional actor-only slice (details still loading). */
  pendingDetailsFronts?: ReadonlySet<string>;
}) {
  const { fontScale, width: windowWidth } = useWindowDimensions();
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const onOffsetCorrection = useContext(DeckSectionScrollContext);
  const keys = useMemo(() => cards.map((card) => card.front), [cards]);
  const { bottomSpacerHeight, end, measure, start, topSpacerHeight } =
    useSectionAccordionMeasuredRowWindow({
      keys,
      // Two text lines plus padding/border is only an estimate for unseen rows. The row remains
      // unconstrained and reports its actual height, including any number of wrapped pinyin lines.
      estimatedRowHeight: 25 + 44 * fontScale,
      geometryKey: `${containerWidth ?? windowWidth}:${fontScale}:${isEditing}`,
      onOffsetCorrection,
    });
  return (
    <View
      onLayout={({ nativeEvent: { layout } }) => {
        if (layout.width > 0) {
          setContainerWidth(layout.width);
        }
      }}
      testID="deck-list-window"
    >
      {topSpacerHeight > 0 && (
        <View style={{ height: topSpacerHeight }} testID="deck-list-leading-space" />
      )}
      {cards.slice(start, end).map((card) => (
        <View
          key={card.front}
          onLayout={({ nativeEvent: { layout } }) => measure(card.front, layout.height)}
          testID={`deck-list-row:${card.front}`}
        >
          <DeckPageItemRow
            graphemeCard={card}
            isDetailsPending={pendingDetailsFronts?.has(card.front) ?? false}
            isEditing={isEditing}
            onRemoveCard={onRemoveCard}
          />
        </View>
      ))}
      {bottomSpacerHeight > 0 && (
        <View style={{ height: bottomSpacerHeight }} testID="deck-list-trailing-space" />
      )}
      {footer}
    </View>
  );
}

const LIST_SKELETON_GLOSS_WIDTHS = ["w-32", "w-24", "w-40", "w-28"];

/**
 * Loading placeholder with `DeckPageItemRow`'s geometry: bordered `px-4 py-3` rows holding a
 * `text-3xl` glyph beside a `text-sm` pinyin line over a `text-base` gloss line.
 */
export function DeckCardSectionListSkeleton({
  maxRowCount = 6,
  rowCount,
}: {
  maxRowCount?: number;
  rowCount: number;
}) {
  return (
    <View testID="deck-list-skeleton">
      {Array.from({ length: Math.min(maxRowCount, rowCount) }, (_, row) => (
        <View className="flex-row items-center gap-3 border-b border-border px-4 py-3" key={row}>
          <Skeleton className="size-9" />
          <View className="flex-1">
            <View className="h-5 justify-center">
              <Skeleton className="h-3.5 w-16" />
            </View>
            <View className="h-6 justify-center">
              <Skeleton
                className={cn(
                  "h-4",
                  LIST_SKELETON_GLOSS_WIDTHS[row % LIST_SKELETON_GLOSS_WIDTHS.length],
                )}
              />
            </View>
          </View>
        </View>
      ))}
    </View>
  );
}
